import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import {
  FaMapMarkerAlt,
  FaPhoneAlt,
  FaEnvelope,
  FaClock,
  FaTelegram,
  FaInstagram,
  FaLinkedinIn,
  FaGlobe,
  FaYoutube,
  FaHome,
  FaArrowRight,
  FaGem,
  FaArrowLeft,
  FaVideo,
} from "react-icons/fa";
import { MdOutlineSupportAgent, MdEmail } from "react-icons/md";
import { HiOutlineLocationMarker } from "react-icons/hi";
import { Link } from "react-router-dom";
import FixedPlugin from "components/fixedPlugin/FixedPlugin";
import LanguageSwitcher from "components/switcher/LanguageSwitcher";
import ContactForm from "./ContactForm";

const ContactPage = () => {
  const { t } = useTranslation();

  const contactInfo = [
    {
      icon: <FaMapMarkerAlt className="h-5 w-5 md:h-6 md:w-6" />,
      title: t("contact.address"),
      details: [t("contact.addressLine1"), ""],
      gradient: "from-blue-500 to-cyan-500",
      bgGradient:
        "from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20",
    },
    {
      icon: <FaPhoneAlt className="h-5 w-5 md:h-6 md:w-6" />,
      title: t("contact.phone"),
      details: [t("contact.phone1"), ""],
      gradient: "from-gray-500 to-navy-500",
      bgGradient:
        "from-gray-50 to-navy-50 dark:from-gray-900/20 dark:to-navy-900/20",
    },
    {
      icon: <FaEnvelope className="h-5 w-5 md:h-6 md:w-6" />,
      title: t("contact.email"),
      details: [t("contact.email1"), t("contact.email2")],
      gradient: "from-purple-500 to-pink-500",
      bgGradient:
        "from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20",
    },
    {
      icon: <FaClock className="h-5 w-5 md:h-6 md:w-6" />,
      title: t("contact.hours"),
      details: [t("contact.hours1"), t("contact.hours2"), t("contact.hours3")],
      gradient: "from-orange-500 to-amber-500",
      bgGradient:
        "from-orange-50 to-amber-50 dark:from-orange-900/20 dark:to-amber-900/20",
    },
  ];

  const socialLinks = [
    {
      icon: <FaTelegram className="h-6 w-6 md:h-7 md:w-7" />,
      href: "https://t.me/vitalplus",
      label: "Telegram",
      color: "from-blue-400 to-blue-600",
      bgColor: "bg-blue-500",
      hoverColor: "hover:bg-blue-600",
    },
    {
      icon: <FaInstagram className="h-6 w-6 md:h-7 md:w-7" />,
      href: "https://instagram.com/vitalplus",
      label: "Instagram",
      color: "from-pink-400 to-purple-600",
      bgColor: "bg-gradient-to-r from-pink-500 to-purple-500",
      hoverColor: "hover:from-pink-600 hover:to-purple-600",
    },
    {
      icon: <FaLinkedinIn className="h-6 w-6 md:h-7 md:w-7" />,
      href: "https://linkedin.com/company/vitalplus",
      label: "LinkedIn",
      color: "from-blue-500 to-blue-700",
      bgColor: "bg-blue-600",
      hoverColor: "hover:bg-blue-700",
    },
    {
      icon: <FaYoutube className="h-6 w-6 md:h-7 md:w-7" />,
      href: "https://youtube.com/@vitalplus",
      label: "YouTube",
      color: "from-red-500 to-red-600",
      bgColor: "bg-red-600",
      hoverColor: "hover:bg-red-700",
    },
    {
      icon: <FaVideo className="h-6 w-6 md:h-7 md:w-7" />,
      href: "https://aparat.com/vitalplus",
      label: "Aparat",
      color: "from-orange-400 to-orange-600",
      bgColor: "bg-orange-500",
      hoverColor: "hover:bg-orange-600",
    },
  ];

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-navy-900 dark:via-navy-800 dark:to-navy-900">
      <FixedPlugin />

      {/* --- Header with Back Button --- */}
      <header className="sticky top-0 z-50 bg-white/80 shadow-sm backdrop-blur-md dark:bg-navy-900/80">
        <div className="container mx-auto flex items-center justify-between px-4 py-3">
          <Link
            to="/"
            className="group flex items-center gap-2 rounded-full bg-brand-50 px-4 py-2 text-sm font-medium text-brand-600 transition-all hover:bg-brand-100 dark:bg-brand-400/30 dark:text-brand-100 dark:hover:bg-brand-500/50"
          >
            <FaArrowLeft className="h-4 w-4 transition-transform group-hover:-translate-x-1 rtl:rotate-180" />
            <span>{t("about.backHome")}</span>
          </Link>
          <div className="flex gap-2">
            <LanguageSwitcher />
            <div className="flex items-center gap-2">
              <span
                dir="ltr"
                className="text-sm font-bold text-gray-700 dark:text-gray-200"
              >
                VITAL+
              </span>
              <FaGem className="h-5 w-5 text-brand-500" />
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-brand-600 via-purple-600 to-pink-600 py-16 dark:from-brand-800 dark:via-purple-800 dark:to-pink-800 md:py-24">
        <div className="absolute inset-0 bg-cover bg-center opacity-10" />
        <div className="from-black/30 via-transparent to-black/50 absolute inset-0 bg-gradient-to-b" />

        <div className="container relative z-10 mx-auto px-4">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="mx-auto max-w-3xl text-center text-white"
          >
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              animate={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.3, type: "spring", bounce: 0.5 }}
              className="mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-2xl bg-white/20 shadow-2xl ring-2 ring-white/30 backdrop-blur-sm md:mb-6 md:h-24 md:w-24 md:rounded-3xl"
            >
              <MdOutlineSupportAgent className="h-10 w-10 text-white md:h-12 md:w-12" />
            </motion.div>

            <div className="mb-3 flex items-center justify-center gap-2 text-sm text-white/70">
              <Link to="/" className="transition-colors hover:text-white">
                {t("contact.home")}
              </Link>
              <span>/</span>
              <span className="text-white">{t("contact.title")}</span>
            </div>

            <h1 className="mb-3 text-3xl font-bold md:mb-4 md:text-5xl lg:text-6xl">
              {t("contact.title")}
            </h1>
            <p className="mx-auto max-w-2xl px-4 text-sm text-white/90 md:text-lg">
              {t("contact.subtitle")}
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="mt-4 flex flex-wrap items-center justify-center gap-3 md:mt-6 md:gap-4"
          >
            <Link
              to="/company/companies/create"
              className="group inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-brand-600 transition-all hover:scale-105 hover:shadow-2xl hover:shadow-white/30 dark:bg-white/10 dark:text-white dark:backdrop-blur-sm dark:hover:bg-white/20 md:rounded-2xl md:px-8 md:py-4"
            >
              <FaArrowRight className="h-3 w-3 transition-transform group-hover:translate-x-1 md:h-4 md:w-4" />
              <span className="text-sm font-semibold md:text-base">
                {t("about.cta")}
              </span>
            </Link>
            <Link
              to="/about"
              className="inline-flex items-center gap-2 rounded-xl border-2 border-white/30 px-6 py-3 text-sm text-white backdrop-blur-sm transition-all hover:bg-white/10 hover:shadow-2xl md:rounded-2xl md:px-8 md:py-4 md:text-base"
            >
              {t("contact.about")}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Breadcrumb */}
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
          <Link
            to="/"
            className="transition-colors hover:text-brand-500 dark:hover:text-brand-400"
          >
            <FaHome className="h-3 w-3" />
          </Link>
          <span>/</span>
          <span className="text-gray-700 dark:text-gray-300">
            {t("contact.title")}
          </span>
        </div>
      </div>

      {/* Contact Info Cards */}
      <section className="container mx-auto -mt-6 px-4 md:-mt-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="grid grid-cols-2 gap-4 md:gap-6 lg:grid-cols-4"
        >
          {contactInfo.map((info, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              transition={{ delay: index * 0.1, type: "spring" }}
              viewport={{ once: true }}
              className={`group relative overflow-hidden rounded-xl bg-gradient-to-br md:rounded-2xl ${info.bgGradient} p-5 shadow-xl backdrop-blur-lg transition-all hover:-translate-y-2 hover:shadow-2xl dark:bg-navy-800/80 dark:shadow-xl md:p-6`}
            >
              <div
                className={`absolute inset-0 bg-gradient-to-br ${info.gradient} opacity-0 transition-opacity duration-500 group-hover:opacity-5`}
              />
              <div className="relative">
                <div
                  className={`mb-3 inline-flex rounded-xl bg-gradient-to-br md:mb-4 md:rounded-2xl ${info.gradient} p-2.5 text-white shadow-lg transition-all group-hover:scale-110 group-hover:shadow-xl md:p-3`}
                >
                  {info.icon}
                </div>
                <h3 className="mb-1 text-base font-bold text-gray-900 dark:text-white md:mb-2 md:text-lg">
                  {info.title}
                </h3>
                {info.details.map((detail, idx) => (
                  <p
                    key={idx}
                    className="text-xs leading-relaxed text-gray-800 dark:text-gray-300 md:text-sm"
                  >
                    {detail}
                  </p>
                ))}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </section>

      {/* Contact Form + Quick Contact + Map Section */}
      <section className="container mx-auto h-full px-4 py-12 md:py-20">
        <div className="grid gap-8 md:gap-12 lg:grid-cols-2">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="h-full"
          >
            <ContactForm />
          </motion.div>

          {/* Quick Contact + Social */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="h-full"
          >
            <div className="rounded-2xl bg-white/80 p-5 shadow-2xl backdrop-blur-lg dark:bg-navy-800/80 md:rounded-3xl md:p-8">
              <div className="mb-4 flex items-center gap-3 md:mb-6">
                <div className="h-1 w-8 rounded-full bg-gradient-to-r from-brand-500 to-purple-500 md:w-12" />
                <h3 className="text-base font-bold text-gray-900 dark:text-white md:text-lg">
                  {t("contact.quickContact")}
                </h3>
              </div>

              <div className="space-y-3 md:space-y-4">
                {/* Response Time */}
                <div className="rounded-xl bg-gradient-to-br from-brand-50 to-purple-50 p-3 transition-all hover:shadow-md dark:from-brand-900/20 dark:to-purple-900/20 md:rounded-2xl md:p-4">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="flex-shrink-0 rounded-full bg-gradient-to-r from-brand-500 to-purple-500 p-2 text-white shadow-lg md:p-3">
                      <FaClock className="h-4 w-4 md:h-5 md:w-5" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 md:text-sm">
                        {t("contact.responseTime")}
                      </p>
                      <p className="text-sm font-bold text-gray-900 dark:text-white md:text-base">
                        {t("contact.responseTimeValue")}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="rounded-xl bg-gradient-to-br from-blue-50 to-cyan-50 p-3 transition-all hover:shadow-md dark:from-blue-900/20 dark:to-cyan-900/20 md:rounded-2xl md:p-4">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="flex-shrink-0 rounded-full bg-gradient-to-r from-blue-500 to-cyan-500 p-2 text-white shadow-lg md:p-3">
                      <MdEmail className="h-4 w-4 md:h-5 md:w-5" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 md:text-sm">
                        {t("contact.email")}
                      </p>
                      <div className="flex flex-col gap-2">
                        <a
                          href="mailto:info@assessment.ir"
                          className="text-sm font-bold text-gray-900 transition-colors hover:text-brand-500 dark:text-white dark:hover:text-brand-400 md:text-base"
                        >
                          {t("contact.email1")}
                        </a>
                        <a
                          href="mailto:info@assessment.ir"
                          className="text-sm font-bold text-gray-900 transition-colors hover:text-brand-500 dark:text-white dark:hover:text-brand-400 md:text-base"
                        >
                          {t("contact.email2")}
                        </a>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Phone */}
                <div className="to-emerald-50 dark:to-emerald-900/20 rounded-xl bg-gradient-to-br from-green-50 p-3 transition-all hover:shadow-md dark:from-green-900/20 md:rounded-2xl md:p-4">
                  <div className="flex items-center gap-3 md:gap-4">
                    <div className="to-emerald-500 flex-shrink-0 rounded-full bg-gradient-to-r from-green-500 p-2 text-white shadow-lg md:p-3">
                      <FaPhoneAlt className="h-4 w-4 md:h-5 md:w-5" />
                    </div>
                    <div>
                      <p className="text-xs text-gray-600 dark:text-gray-400 md:text-sm">
                        {t("contact.phone")}
                      </p>
                      <a
                        href="tel:02112345678"
                        className="text-sm font-bold text-gray-900 transition-colors hover:text-brand-500 dark:text-white dark:hover:text-brand-400 md:text-base"
                      >
                        {t("contact.phone1")}
                      </a>
                    </div>
                  </div>
                </div>

                {/* Social Links (کوچیک شده - داخل Quick Contact) */}
                <div className="border-t border-gray-100 pt-4 dark:border-navy-700 md:pt-5">
                  <div className="mb-3 flex items-center gap-2">
                    <div className="h-0.5 w-5 rounded-full bg-gradient-to-r from-brand-500 to-purple-500" />
                    <p className="text-xs font-medium text-gray-600 dark:text-gray-400">
                      {t("contact.social")}
                    </p>
                  </div>

                  <div className="flex flex-wrap items-center gap-2">
                    {socialLinks.map((social, index) => (
                      <motion.a
                        key={index}
                        href={social.href}
                        target="_blank"
                        rel="noopener noreferrer"
                        initial={{ opacity: 0, scale: 0.8 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        transition={{ delay: index * 0.05, type: "spring" }}
                        viewport={{ once: true }}
                        title={social.label}
                        className={`group relative flex h-9 w-9 items-center justify-center rounded-lg ${social.bgColor} text-sm text-white shadow-md transition-all duration-300 hover:scale-110 hover:shadow-lg ${social.hoverColor} md:h-10 md:w-10`}
                      >
                        <span className="text-[14px] md:text-[15px]">
                          {social.icon}
                        </span>
                      </motion.a>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Map  */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="mt-8"
        >
          <div className="mb-4 md:mb-6">
            <div className="dark:from-transparent dark:to-transparent mb-2 inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-brand-100 to-purple-100 px-3 py-1 dark:bg-brand-500/30 md:px-4 md:py-1.5">
              <HiOutlineLocationMarker className="h-3 w-3 text-brand-500 dark:text-brand-200 md:h-4 md:w-4" />
              <span className="text-[10px] font-medium text-brand-600 dark:text-brand-200 md:text-xs">
                {t("contact.location")}
              </span>
            </div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white md:text-3xl">
              {t("contact.mapTitle")}
            </h2>
            <p className="mt-1 text-sm text-gray-600 dark:text-gray-300 md:mt-2 md:text-base">
              {t("contact.mapDesc")}
            </p>
          </div>

          <div className="overflow-hidden rounded-2xl bg-white/80 p-1.5 shadow-2xl backdrop-blur-lg transition-all hover:shadow-3xl dark:bg-navy-800/80 md:rounded-3xl md:p-2">
            <div className="aspect-[16/4] w-full overflow-hidden rounded-xl md:rounded-2xl">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1607.1962588297793!2d50.044122396762994!3d36.327035699999996!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3f8b55128bfd6fd9%3A0xc406d8111203ab80!2z2YXYqNuM2YYg2KfYqti12KfZhCDYotiz2YXYp9mG!5e0!3m2!1sfa!2s!4v1785059306392!5m2!1sfa!2s"
                className="h-full w-full"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Office Location"
              ></iframe>
            </div>
          </div>
        </motion.div>
      </section>

      {/* CTA Section */}
      <section className="container mx-auto px-4 pb-12 md:pb-20">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true }}
          className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-brand-500 to-purple-600 px-6 py-12 text-center text-white shadow-2xl dark:from-brand-700 dark:to-purple-800 md:rounded-3xl md:p-16"
        >
          <div className="absolute inset-0 opacity-10">
            <motion.div
              animate={{ rotate: 360 }}
              transition={{ duration: 60, repeat: Infinity, ease: "linear" }}
              className="absolute -left-20 -top-20 h-60 w-60 rounded-full bg-white md:-left-32 md:-top-32 md:h-96 md:w-96"
            />
            <motion.div
              animate={{ rotate: -360 }}
              transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
              className="absolute -bottom-20 -right-20 h-60 w-60 rounded-full bg-white md:-bottom-32 md:-right-32 md:h-96 md:w-96"
            />
            <div className="absolute left-1/2 top-1/2 h-60 w-60 -translate-x-1/2 -translate-y-1/2 rounded-full bg-white md:h-96 md:w-96" />
          </div>

          <div className="relative z-10">
            <motion.div
              initial={{ scale: 0, rotate: -180 }}
              whileInView={{ scale: 1, rotate: 0 }}
              transition={{ delay: 0.2, type: "spring", bounce: 0.5 }}
              className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-xl bg-white/20 shadow-2xl ring-2 ring-white/30 backdrop-blur-sm md:mb-6 md:h-20 md:w-20 md:rounded-2xl"
            >
              <FaGlobe className="h-8 w-8 text-white md:h-10 md:w-10" />
            </motion.div>

            <h2 className="mb-3 text-xl font-bold md:mb-4 md:text-3xl">
              {t("contact.ctaTitle")}
            </h2>
            <p className="mx-auto mb-6 max-w-2xl px-4 text-sm text-white/90 md:mb-8 md:text-lg">
              {t("contact.ctaDesc")}
            </p>
            <div className="flex flex-wrap items-center justify-center gap-3 md:gap-4">
              <a
                href="tel:02832880000"
                className="group inline-flex items-center gap-2 rounded-xl bg-white px-6 py-3 text-brand-600 transition-all hover:scale-105 hover:shadow-2xl hover:shadow-white/30 dark:bg-white/10 dark:text-white dark:backdrop-blur-sm dark:hover:bg-white/20 md:rounded-2xl md:px-8 md:py-4"
              >
                <span className="text-sm font-semibold md:text-base">
                  {t("contact.callUs")}
                </span>
                <FaPhoneAlt className="h-3 w-3 transition-transform group-hover:scale-110 md:h-4 md:w-4" />
              </a>
              <a
                href="mailto:info@vitalplus.ir"
                className="inline-flex items-center gap-2 rounded-xl border-2 border-white/30 px-6 py-3 text-sm text-white backdrop-blur-sm transition-all hover:bg-white/10 hover:shadow-2xl md:rounded-2xl md:px-8 md:py-4 md:text-base"
              >
                <span>{t("contact.emailUs")}</span>
                <FaEnvelope className="h-3 w-3 md:h-4 md:w-4" />
              </a>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  );
};

export default ContactPage;
